// The almanac (docs/23 Y8): the World's date at a glance — the realm's own name for the season
// with a pixel glyph of the real one, the week as twelve ticks, the year in its reckoning, and a
// wheel of the whole year with a marker at today; under it how the air feels and what the season
// usually brings in this country, and the weather over the realm this week (docs/24 K5).
// `compact` is the one-line chip the HUD and lobbies use.
import type { ReactNode } from 'react';
import { BIOME_WORDS, WEEKS_PER_SEASON, ordinal, seasonKey, seasonWordIn, skyWords, weekPhrase, yearPhrase, type ClockRow, type SeasonKey } from '../../core/story/calendar';
import './almanac.css';

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** A 9×9 pixel glyph per real season: a blossom, a sun, a leaf, a snowflake. */
const GLYPHS: Record<SeasonKey, string[]> = {
  spring: [
    '...aa....',
    '..abba...',
    '.aaabaaa.',
    'abbaaabba',
    '.aaacaaa.',
    '..abbba..',
    '..aa.aa..',
    '....g....',
    '...ggg...',
  ],
  summer: [
    '....a....',
    '.a..a..a.',
    '..abbba..',
    '.abbbbba.',
    'aabbcbbaa',
    '.abbbbba.',
    '..abbba..',
    '.a..a..a.',
    '....a....',
  ],
  autumn: [
    '......aa.',
    '....aaba.',
    '...abbba.',
    '..abbcba.',
    '.abbcbba.',
    '.abcbba..',
    '.acbaa...',
    'cc.......',
    'c........',
  ],
  winter: [
    '....a....',
    '.a.aba.a.',
    '..aaaaa..',
    '.aabcbaa.',
    'abacccaba',
    '.aabcbaa.',
    '..aaaaa..',
    '.a.aba.a.',
    '....a....',
  ],
};
const GLYPH_COLOURS: Record<SeasonKey, Record<string, string>> = {
  spring: { a: '#f4a6c4', b: '#fff0f6', c: '#ffd166', g: '#5fae5a' },
  summer: { a: '#ffbf3b', b: '#ffe08a', c: '#fff4d6' },
  autumn: { a: '#c4502a', b: '#e8873a', c: '#7a3a1c' },
  winter: { a: '#a8d4ff', b: '#e8f4ff', c: '#ffffff' },
};

export function SeasonGlyph({ season, scale = 2, className = '' }: { season: SeasonKey; scale?: number; className?: string }) {
  const rows = GLYPHS[season], colours = GLYPH_COLOURS[season];
  return (
    <svg className={`fk-season-glyph ${className}`} width={9 * scale} height={9 * scale} viewBox="0 0 9 9" shapeRendering="crispEdges" aria-hidden="true">
      {rows.flatMap((r, y) => [...r].map((ch, x) => (colours[ch] ? <rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} fill={colours[ch]} /> : null)))}
    </svg>
  );
}

/** The year as a ring of 48 weeks coloured by season, with today marked. */
function YearWheel({ clock, size = 44 }: { clock: ClockRow; size?: number }) {
  const r = size / 2 - 3, c = size / 2;
  const seg = (i: number) => {
    const a0 = (i / 48) * Math.PI * 2 - Math.PI / 2, a1 = ((i + 0.82) / 48) * Math.PI * 2 - Math.PI / 2;
    return `M ${c + r * Math.cos(a0)} ${c + r * Math.sin(a0)} A ${r} ${r} 0 0 1 ${c + r * Math.cos(a1)} ${c + r * Math.sin(a1)}`;
  };
  const key = seasonKey(clock.season) ?? 'spring';
  return (
    <svg className="fk-almanac__wheel" width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      {Array.from({ length: 48 }, (_, i) => (
        <path key={i} d={seg(i)} className={`fk-almanac__arc fk-almanac__arc--${seasonKey(Math.floor(i / WEEKS_PER_SEASON))} ${i === clock.season * WEEKS_PER_SEASON + clock.week - 1 ? 'is-now' : i < clock.season * WEEKS_PER_SEASON + clock.week - 1 ? 'is-past' : ''}`} />
      ))}
      <g transform={`translate(${c - 6.75} ${c - 6.75})`}><SeasonGlyph season={key} scale={1.5} /></g>
    </svg>
  );
}

/** The week of the season as twelve ticks. */
function WeekTicks({ week }: { week: number }) {
  return (
    <span className="fk-almanac__ticks" aria-hidden="true">
      {Array.from({ length: WEEKS_PER_SEASON }, (_, i) => <i key={i} className={i + 1 < week ? 'past' : i + 1 === week ? 'now' : ''} />)}
    </span>
  );
}

export function Almanac({ clock, actions, className = '', testId = 'almanac' }: { clock: ClockRow | undefined; actions?: ReactNode; className?: string; testId?: string }) {
  if (!clock) return null;
  const key = seasonKey(clock.season) ?? 'spring';
  return (
    <section className={`fk-almanac fk-almanac--${key} ${className}`} aria-label="The World's date" data-testid={testId} data-season={key} data-week={clock.week} data-year={clock.year}>
      <div className="fk-almanac__head">
        <YearWheel clock={clock} />
        <div className="fk-almanac__names">
          <span className="fk-almanac__eyebrow">{seasonWordIn(key, clock.biome)} · {ordinal(clock.week)} week of {WEEKS_PER_SEASON}</span>
          <b className="fk-almanac__season" data-testid="almanac-season">{clock.seasonName}</b>
          <span className="fk-almanac__year" data-testid="almanac-year">Year {yearPhrase(clock)}</span>
        </div>
      </div>
      <WeekTicks week={clock.week} />
      <p className="fk-almanac__weather">
        <span className={`fk-almanac__temp fk-almanac__temp--${clock.temperature.toLowerCase()}`}>{clock.temperature}</span>
        <span className="fk-almanac__climate">{cap(clock.climate)}</span>
      </p>
      {clock.weather && (
        <p className="fk-almanac__week" data-testid="almanac-weather" data-weather={clock.weather} title="The realm's weather this week. A week passes with every finished fight.">
          This week: <b>{cap(skyWords(clock.weather, clock.biome))}</b> across the realm
        </p>
      )}
      <p className="fk-almanac__land">{BIOME_WORDS[clock.biome] ?? cap(clock.biome)}</p>
      {actions && <div className="fk-almanac__actions">{actions}</div>}
    </section>
  );
}

/** One line: glyph, "2nd week of Bloomtide", year — for the HUD, the local map and lobbies. */
export function AlmanacChip({ clock, year = true, className = '' }: { clock: ClockRow | undefined; year?: boolean; className?: string }) {
  if (!clock) return null;
  const key = seasonKey(clock.season) ?? 'spring';
  return (
    <span className={`fk-almanac-chip fk-almanac-chip--${key} ${className}`} data-testid="almanac-chip" title={`${weekPhrase(clock)}, in the year ${yearPhrase(clock)} — ${clock.temperature.toLowerCase()}`}>
      <SeasonGlyph season={key} scale={1.5} />
      <span>{weekPhrase(clock)}</span>
      {year && <span className="fk-almanac-chip__year">· {clock.year}</span>}
    </span>
  );
}
